import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { TaskStatus } from '../../models/response/task-status';
import { TaskType } from '../../models/response/task-type';
import { City } from '../../models/response/address/city';
import { Buyer } from '../../models/response/buyer/buyer';
import { BuyerCreateComponent } from '../buyer-create/buyer-create.component';
import { BuyerRequest } from '../../models/request/buyer/buyer-request';
import { BuyerService } from '../../services/buyer.service';
import { CommonService } from '../../services/common.service';

@Component({
  selector: 'ngx-buyer-create-v2',
  templateUrl: './buyer-create-v2.component.html',
  styleUrls: ['./buyer-create-v2.component.scss'],
})
export class BuyerCreateV2Component {
  @ViewChild(BuyerCreateComponent) buyerCreate: BuyerCreateComponent;

  activeSection: 'C' | 'A' | 'N' | 'I' = 'C';
  isBlank = true;
  isCreateMode = false;
  isSectionView = false;
  isBulkUpdateMode = false;
  bulkBuyerIds: number[] = [];

  @Input() cities: City[];
  @Input() taskTypes: TaskType[];
  @Input() taskStatuses: TaskStatus[];

  @Output() editDone = new EventEmitter();
  @Output() newDone = new EventEmitter();
  @Output() reserveBuyer = new EventEmitter();
  @Output() ownSet = new EventEmitter();
  @Output() navigateBuyerEvent = new EventEmitter();
  @Output() backToList = new EventEmitter();

  constructor(private buyerService: BuyerService,
              private commonService: CommonService) {
  }

  setActiveSection(section: 'C' | 'A' | 'N' | 'I') {
    if (this.isSectionView && this.activeSection === section && !this.isCreateMode) {
      this.isSectionView = false;
      return;
    }
    this.activeSection = section;
    if (!this.isBlank && !this.isCreateMode) {
      this.isSectionView = true;
    }
  }

  setBuyer(buyer: Buyer) {
    this.isBulkUpdateMode = false;
    this.bulkBuyerIds = [];
    this.activeSection = 'C';
    this.isBlank = !buyer;
    this.isCreateMode = false;
    this.isSectionView = false;
    if (buyer) {
      this.buyerCreate?.setBuyer(buyer);
    } else {
      this.buyerCreate?.clear();
    }
  }

  setBuyers(buyers: Buyer[]) {
    this.buyerCreate?.setBuyers(buyers);
  }

  new() {
    this.isBulkUpdateMode = false;
    this.bulkBuyerIds = [];
    this.activeSection = 'C';
    this.isBlank = false;
    this.isCreateMode = true;
    this.isSectionView = false;
    this.buyerCreate?.setBuyer(null);
    this.buyerCreate?.new();
  }

  startBulkUpdate(buyerIds: number[], mode: 'B' | 'T') {
    this.activeSection = 'C';
    this.isBlank = false;
    this.isCreateMode = false;
    this.isSectionView = false;
    this.isBulkUpdateMode = true;
    this.bulkBuyerIds = buyerIds || [];
    this.buyerCreate?.startBulkUpdate(mode);
  }

  setBuyerMode(mode: 'buyer' | 'tenant') {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return;
    }
    const buyerCode = mode === 'tenant' ? 'T' : 'B';
    const buyerId = this.buyerCreate?.data?.buyerId;
    if (this.isBulkUpdateMode) {
      this.bulkUpdateBuyerMode(mode, buyerCode);
      return;
    }
    if (buyerId) {
      this.buyerCreate.isUpdating = true;
      this.buyerService.update({ buyerId, buyerCode } as BuyerRequest).subscribe(
        result => {
          this.buyerCreate.isUpdating = false;
          this.applyBuyerMode(mode, buyerCode);
          this.commonService.info('Updated buyer success.');
          this.editDone.emit(result);
        },
        error => {
          this.buyerCreate.isUpdating = false;
          this.commonService.warningHtml(error.message);
        },
      );
      return;
    }
    this.applyBuyerMode(mode, buyerCode);
  }

  private bulkUpdateBuyerMode(mode: 'buyer' | 'tenant', buyerCode: 'B' | 'T') {
    if (!this.bulkBuyerIds || this.bulkBuyerIds.length <= 1) {
      this.commonService.warning('Please select more than one buyer.');
      return;
    }
    this.buyerCreate.isUpdating = true;
    this.buyerService.bulkUpdate({
      buyerIds: this.bulkBuyerIds,
      request: { buyerCode } as BuyerRequest,
    }).subscribe(
      result => {
        this.buyerCreate.isUpdating = false;
        this.applyBuyerMode(mode, buyerCode);
        this.commonService.info('Updated selected buyers success.');
        this.editDone.emit(result);
      },
      error => {
        this.buyerCreate.isUpdating = false;
        this.commonService.warningHtml(error.message);
      },
    );
  }

  private applyBuyerMode(mode: 'buyer' | 'tenant', buyerCode: 'B' | 'T') {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return;
    }
    const isSwitchingBuyerMode = form.get('buyerCode').value !== buyerCode;
    form.get('buyer')?.setValue(mode === 'buyer', { emitEvent: false });
    form.get('tenant')?.setValue(mode === 'tenant', { emitEvent: false });
    form.get('buyerCode')?.setValue(buyerCode);
    if (isSwitchingBuyerMode) {
      form.get('preferredAreas')?.setValue([]);
      form.get('preferredAreas')?.markAsPristine();
    }
    form.get('buyer')?.markAsPristine();
    form.get('tenant')?.markAsPristine();
    form.get('isFirstHome')?.markAsPristine();
    form.get('buyerCode')?.markAsDirty();
    if (this.buyerCreate?.data) {
      this.buyerCreate.data.buyerCode = buyerCode;
      if (isSwitchingBuyerMode) {
        this.buyerCreate.data.preferredAreas = [];
      }
    }
  }

  toggleWot() {
    const control = this.buyerCreate?.buyerForm?.get('isWot');
    if (!control) {
      return;
    }
    control.setValue(control.value !== true);
  }

  activeStatusLabel(): 'ACTIVE' | 'INACTIVE' {
    return this.buyerCreate?.buyerForm?.get('isActive')?.value === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE';
  }

  toggleActiveStatus() {
    const control = this.buyerCreate?.buyerForm?.get('isActive');
    if (!control) {
      return;
    }
    control.setValue(this.activeStatusLabel() === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE');
  }

  navigateBuyer(forward: boolean) {
    this.buyerCreate?.navigate(forward);
  }

  noAnswerAndNext() {
    const callControl = this.buyerCreate?.buyerForm?.get('call');
    if (callControl) {
      callControl.setValue(false);
    }
    if (this.buyerCreate?.data?.buyerId) {
      this.buyerCreate.logContactStatus(this.buyerCreate.contactLogType.NO_ANSWER);
      return;
    }
    this.navigateBuyer(true);
  }

  compactBuyerCode(): string {
    const form = this.buyerCreate?.buyerForm;
    if (!form || this.isBlank) {
      return '';
    }
    const buyerCode = form.get('buyerCode')?.value || '';
    const classification = this.classificationCode();
    const rate = this.displayRate();
    const status = [
      form.get('isCommon')?.value ? 'C' : '',
      form.get('isAddress')?.value ? 'A' : '',
      form.get('isNumber')?.value ? 'N' : '',
      form.get('isInspection')?.value ? 'I' : '',
    ].join('');
    return [buyerCode, classification, rate, status]
      .filter(value => value !== undefined && value !== null && value !== '')
      .join('.');
  }

  private displayRate(): number {
    const dataRate = this.buyerCreate?.data?.rate;
    if (this.buyerCreate?.data?.buyerId && dataRate !== undefined && dataRate !== null) {
      return dataRate;
    }
    return this.buyerCreate?.buyerForm?.get('rate')?.value || 0;
  }

  private classificationCode(): string {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return '';
    }
    return [
      form.get('isFirstHome')?.value ? 'F' : '',
      form.get('isInvestor')?.value ? 'I' : '',
      form.get('isValueBuyer')?.value ? 'VB' : '',
      form.get('isDeveloper')?.value ? 'D' : '',
    ].filter(value => value !== '').join('-');
  }

  back() {
    this.backToList.emit();
  }

  save() {
    if (this.isBulkUpdateMode) {
      this.buyerCreate?.submitBulkUpdate(this.bulkBuyerIds, result => this.editDone.emit(result));
      return;
    }
    this.buyerCreate?.submit();
  }

  createTask() {
    this.buyerCreate?.createTask();
  }

  createTaskForCurrentAddress() {
    this.buyerCreate?.createTaskForCurrentAddress();
  }

  appointmentAddress(buyerTask: any): string {
    const task = buyerTask?.task;
    if (task?.appointmentAddress) {
      return task.appointmentAddress;
    }
    const address = task?.prospect?.address || task?.buyer?.address;
    const formattedAddress = this.buyerCreate?.getAddressName(address);
    return this.formatValue(formattedAddress) || '-';
  }

  sectionSummary(section: 'C' | 'A' | 'N' | 'I'): string {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return '';
    }
    switch (section) {
      case 'C':
        return this.joinParts([
          this.formatPropertyBasics(),
          this.formatBudget(),
        ]);
      case 'A':
        return this.joinParts([
          this.formatAddress(),
          form.controls?.enq?.value,
          this.buyerCreate?.preferredAreas?.join(', '),
          this.buyerCreate?.interestPostcode,
        ]);
      case 'N':
        return this.joinParts([
          this.formatValue(form.controls?.enqDate2?.value),
          this.formatPrefixed('NW', form.controls?.noWeek?.value),
          this.formatPrefixed('NH', form.controls?.noHouse?.value),
          form.controls?.tenant?.value ? this.formatTenantTiming() : '',
        ]);
      case 'I':
        return this.joinParts([
          this.formatPrefixed('Appointments', this.buyerCreate?.buyerTasks?.length),
          this.formatPrefixed('Deposits', this.buyerCreate?.buyerDeposits?.length),
          this.nextAppointmentText(),
        ]);
      default:
        return '';
    }
  }

  overviewAddress(): string {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return '';
    }
    return this.formatValue(form.controls?.enqAddress?.value);
  }

  propertyTypeInitial(): string {
    const value = this.buyerCreate?.buyerForm?.controls?.type?.value;
    const label = this.buyerCreate?.propertyType?.[value] || value;
    return label ? String(label).charAt(0).toUpperCase() : '';
  }

  private formatPropertyBasics(): string {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return '';
    }
    return this.joinParts([
      form.controls?.type?.value,
      this.formatPrefixed('Bed', form.controls?.bed?.value),
      this.formatPrefixed('Bath', form.controls?.bath?.value),
      this.formatPrefixed('Car', form.controls?.car?.value),
      this.formatPrefixed('Land', form.controls?.land?.value),
    ]);
  }

  private formatBudget(): string {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return '';
    }
    return this.joinParts([
      this.formatPrefixed('$min', form.controls?.minBuyPrice?.value),
      this.formatPrefixed('$max', form.controls?.maxBuyPrice?.value),
    ]);
  }

  private formatAddress(): string {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return '';
    }
    const district = this.buyerCreate?.filteredDistricts?.find(x => String(x.districtId) === String(form.controls?.districtId?.value))?.name;
    const street = this.buyerCreate?.filteredStreets?.find(x => String(x.streetId) === String(form.controls?.streetId?.value))?.name;
    return this.joinParts([
      this.formatValue(this.buyerCreate?.postcode),
      district,
      street,
      this.formatNumberRange(),
    ]);
  }

  private formatNumberRange(): string {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return '';
    }
    const from = this.formatValue(form.controls?.numberFrom?.value);
    const to = this.formatValue(form.controls?.numberTo?.value);
    if (!from && !to) {
      return '';
    }
    return `${from || '?'}-${to || '?'}`;
  }

  private formatTenantTiming(): string {
    const form = this.buyerCreate?.buyerForm;
    if (!form) {
      return '';
    }
    return this.joinParts([
      this.formatPrefixed('Year In', form.controls?.yearIn?.value),
      this.formatPrefixed('Month In', form.controls?.monthIn?.value),
      this.formatPrefixed('Week Out', form.controls?.weekOut?.value),
      this.formatPrefixed('Day Out', form.controls?.dayOut?.value),
    ]);
  }

  private nextAppointmentText(): string {
    const nextTask = this.buyerCreate?.buyerTasks?.[0];
    if (!nextTask?.task?.toDate) {
      return '';
    }
    const value = new Date(nextTask.task.toDate);
    const dateText = Number.isNaN(value.getTime()) ? '' : value.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return dateText ? `Next ${dateText}` : '';
  }

  private formatPrefixed(label: string, value: any): string {
    const formatted = this.formatValue(value);
    return formatted ? `${label} ${formatted}` : '';
  }

  private formatValue(value: any): string {
    if (value === null || value === undefined || value === '' || value === 0 || value === '0') {
      return '';
    }
    return String(value);
  }

  private joinParts(parts: Array<string>): string {
    return parts.filter(Boolean).join(' | ');
  }
}
